import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Trash2, Upload, Save, ExternalLink, Eye, Rocket, Search, ArrowUp, ArrowDown, Download, LayoutDashboard } from "lucide-react";
import { API, DEFAULT_CONTENT, deepMerge, resolveImage } from "@/lib/content";

axios.defaults.withCredentials = true;

const formatErr = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

const TABS = [
  ["dashboard", "Dashboard"],
  ["business", "Business Info"],
  ["hero", "Hero & Photos"],
  ["services", "Services"],
  ["packages", "Packages"],
  ["story", "Story & Marquee"],
  ["trust", "Trust"],
  ["faq", "FAQ"],
  ["pages", "Pages"],
  ["seo", "SEO"],
  ["nav", "Navigation"],
  ["sections", "Homepage Sections"],
  ["redirects", "Redirects"],
  ["contact", "Contact & Leads Settings"],
  ["leads", "Leads"],
  ["account", "Account"],
];

const inputCls =
  "w-full rounded-lg border border-[#1C1917]/15 bg-white px-4 py-2.5 text-sm text-[#1C1917] focus:border-[#7A2B21]";

export default function Admin() {
  const [auth, setAuth] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isDraft, setIsDraft] = useState(false);

  const draftRef = useRef(draft);
  draftRef.current = draft;

  const [get] = useState(
    () => (path) => path.split(".").reduce((o, k) => o?.[k], draftRef.current)
  );
  const [set] = useState(
    () => (path, val) =>
      setDraft((d) => {
        const copy = structuredClone(d);
        const keys = path.split(".");
        let o = copy;
        keys.slice(0, -1).forEach((k) => (o = o[k]));
        o[keys[keys.length - 1]] = val;
        return copy;
      })
  );

  const [{ F, Lines, Img, Card }] = useState(() => ({
    F: ({ label, path, textarea, hint }) => (
      <label className="block" data-testid={`field-${path}`}>
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#57534E]">{label}</span>
        {textarea ? (
          <textarea rows={3} className={`${inputCls} resize-y`} value={get(path) ?? ""} onChange={(e) => set(path, e.target.value)} />
        ) : (
          <input className={inputCls} value={get(path) ?? ""} onChange={(e) => set(path, e.target.value)} />
        )}
        {hint && <span className="mt-1 block text-[11px] text-[#57534E]/70">{hint}</span>}
      </label>
    ),
    Lines: ({ label, path, hint }) => (
      <label className="block" data-testid={`field-${path}`}>
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#57534E]">{label}</span>
        <textarea
          rows={Math.max(3, (get(path) || []).length)}
          className={`${inputCls} resize-y`}
          value={(get(path) || []).join("\n")}
          onChange={(e) => set(path, e.target.value.split("\n").filter((l) => l.trim() !== ""))}
        />
        <span className="mt-1 block text-[11px] text-[#57534E]/70">{hint || "One item per line."}</span>
      </label>
    ),
    Img: ({ label, path }) => {
      const [busy, setBusy] = useState(false);
      const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", file);
          const r = await axios.post(`${API}/admin/upload`, fd);
          set(path, r.data.url);
          toast.success("Photo uploaded — press Save Changes to publish it.");
        } catch (err) {
          toast.error(formatErr(err.response?.data?.detail));
        } finally {
          setBusy(false);
        }
      };
      return (
        <div data-testid={`image-${path}`}>
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#57534E]">{label}</span>
          <div className="flex items-start gap-4">
            {get(path) ? (
              <img src={resolveImage(get(path))} alt={label} className="h-24 w-40 rounded-lg border border-[#1C1917]/10 object-cover" />
            ) : (
              <div className="flex h-24 w-40 items-center justify-center rounded-lg border border-dashed border-[#1C1917]/15 text-[10px] uppercase tracking-wider text-[#57534E]/50">No photo</div>
            )}
            <div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#2C4C3B] px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#1f3529]">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {busy ? "Uploading..." : "Upload New Photo"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid={`upload-${path}`} />
              </label>
              <p className="mt-2 text-[11px] text-[#57534E]/70">JPG, PNG or WebP, up to 10 MB. Landscape photos work best.</p>
            </div>
          </div>
        </div>
      );
    },
    Card: ({ title, children }) => (
      <div className="rounded-2xl border border-[#1C1917]/10 bg-white p-6 shadow-[0_10px_30px_rgba(28,25,23,0.05)]">
        {title && <h3 className="mb-5 font-display text-lg font-medium text-[#1C1917]">{title}</h3>}
        <div className="space-y-5">{children}</div>
      </div>
    ),
  }));

  useEffect(() => {
    axios.get(`${API}/auth/me`).then((r) => setAuth(r.data)).catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (auth && auth !== false) {
      axios.get(`${API}/admin/draft`).then((r) => {
        setDraft(deepMerge(DEFAULT_CONTENT, r.data.content));
        setIsDraft(r.data.is_draft);
      });
    }
  }, [auth]);

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5]">
        <Loader2 className="animate-spin text-[#7A2B21]" size={32} />
      </div>
    );
  }
  if (auth === false) return <Login onSuccess={setAuth} />;
  if (!draft) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5]">
        <Loader2 className="animate-spin text-[#7A2B21]" size={32} />
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, { content: draft });
      setIsDraft(true);
      toast.success("Draft saved — nothing is live until you press Publish.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await axios.put(`${API}/admin/content`, { content: draft });
      await axios.post(`${API}/admin/publish`);
      setIsDraft(false);
      toast.success("Published! Your changes are live on the website.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    setAuth(false);
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-[#1C1917]/10 bg-[#FAF8F5]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <p className="font-display text-lg font-semibold text-[#1C1917]">Website Admin</p>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#7A2B21]">{auth.email}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {isDraft && (
              <span className="rounded-full bg-[#C5A059]/25 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#7A2B21]" data-testid="draft-badge">
                Unpublished draft
              </span>
            )}
            <a href="/?preview=1" target="_blank" rel="noopener noreferrer" data-testid="admin-preview-button"
              className="inline-flex items-center gap-2 rounded-full border border-[#1C1917]/15 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#1C1917] transition-colors hover:border-[#7A2B21] hover:text-[#7A2B21]">
              <Eye size={13} /> Preview
            </a>
            <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site"
              className="inline-flex items-center gap-2 rounded-full border border-[#1C1917]/15 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#1C1917] transition-colors hover:border-[#7A2B21] hover:text-[#7A2B21]">
              <ExternalLink size={13} /> Live Site
            </a>
            <button onClick={save} disabled={saving} data-testid="admin-save-button"
              className="inline-flex items-center gap-2 rounded-full border border-[#7A2B21] px-5 py-2 text-xs font-bold uppercase tracking-wider text-[#7A2B21] transition-colors hover:bg-[#7A2B21]/10 disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saving ? "Saving..." : "Save Draft"}
            </button>
            <button onClick={publish} disabled={publishing} data-testid="admin-publish-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#2C4C3B] px-5 py-2 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#1f3529] disabled:opacity-60">
              {publishing ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />}
              {publishing ? "Publishing..." : "Publish Live"}
            </button>
            <button onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-[#1C1917]/15 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#1C1917] transition-colors hover:border-[#7A2B21] hover:text-[#7A2B21]">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-6 pb-3">
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                tab === id ? "bg-[#1C1917] text-[#FAF8F5]" : "text-[#57534E] hover:bg-[#1C1917]/5"
              }`}>
              {label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-6 py-10">
        {tab === "hero" && (
          <>
            <Card title="Main Photo & Headline">
              <Img label="Hero background photo" path="hero.image_url" />
              <F label="Small badge text (top)" path="hero.badge" />
              <F label="Brand promise line (gold italic, above the H1)" path="hero.promise_line" />
              <F label="H1 part 1 — main Google heading" path="hero.h1_line1" hint="This is the page's H1. Keep your keywords here." />
              <F label="H1 part 2 (gold italic)" path="hero.h1_line2" />
              <F label="Hero photo alt text (for Google Images)" path="hero.image_alt" hint="Describe the photo plainly, e.g. 'Elderly Sikh parents at their home in Chandigarh'." />
              <F label="Paragraph below headline" path="hero.subtext" textarea />
              <Lines label="Trust strip (3 short phrases)" path="hero.stats" />
              <F label="Default WhatsApp message" path="hero.whatsapp_msg" textarea hint="This message is pre-filled when visitors tap any WhatsApp button." />
            </Card>
            <Card title="Other Photos">
              <Img label="Parent care photo (Services)" path="services.care.image" />
              <F label="Parent care photo alt text" path="services.care.image_alt" />
              <Img label="Property photo (Services)" path="services.property.image" />
              <F label="Property photo alt text" path="services.property.image_alt" />
              <Img label="How It Works photo" path="how.image" />
              <F label="How It Works photo alt text" path="how.image_alt" />
            </Card>
          </>
        )}

        {tab === "services" && (
          <>
            <Card title="Section Heading">
              <F label="Kicker (small label)" path="services.kicker" />
              <div className="grid gap-5 sm:grid-cols-2">
                <F label="Title part 1" path="services.title_line1" />
                <F label="Title part 2 (gold italic)" path="services.title_line2" />
              </div>
              <F label="Intro paragraph" path="services.intro" textarea />
            </Card>
            <Card title="Card 1 — Parent Companionship">
              <F label="Title" path="services.care.title" />
              <F label="Description" path="services.care.body" textarea />
            </Card>
            <Card title="Card 2 — Appointments">
              <F label="Title" path="services.appointments.title" />
              <F label="Description" path="services.appointments.body" textarea />
              <F label="Honest note (non-medical disclaimer)" path="services.appointments.honest_note" textarea />
            </Card>
            <Card title="Card 3 — Property">
              <F label="Title" path="services.property.title" />
              <F label="Description" path="services.property.body" textarea />
            </Card>
            <Card title="Three Small Highlights">
              {[0, 1, 2].map((i) => (
                <div key={i} className="grid gap-3 rounded-xl bg-[#FAF8F5] p-4 sm:grid-cols-2">
                  <F label={`Highlight ${i + 1} title`} path={`services.mini.${i}.title`} />
                  <F label="Text" path={`services.mini.${i}.body`} />
                </div>
              ))}
            </Card>
          </>
        )}

        {tab === "packages" && (
          <>
            <Card title="Section Heading">
              <F label="Kicker" path="packages.kicker" />
              <div className="grid gap-5 sm:grid-cols-2">
                <F label="Title part 1" path="packages.title_line1" />
                <F label="Title part 2 (gold italic)" path="packages.title_line2" />
              </div>
              <F label="Pricing note (right side)" path="packages.note" textarea />
            </Card>
            {get("packages.plans").map((p, i) => (
              <Card key={p.id} title={`Plan ${i + 1} — ${p.name}`}>
                <div className="grid gap-5 sm:grid-cols-3">
                  <F label="Plan name" path={`packages.plans.${i}.name`} />
                  <F label="Price" path={`packages.plans.${i}.price`} hint='e.g. ₹4,999' />
                  <F label="Period" path={`packages.plans.${i}.period`} hint="e.g. /month" />
                </div>
                <F label="Tagline" path={`packages.plans.${i}.tagline`} />
                <Lines label="Features" path={`packages.plans.${i}.features`} hint="One feature per line." />
                <label className="flex items-center gap-2 text-xs font-semibold text-[#57534E]">
                  <input type="checkbox" checked={!!get(`packages.plans.${i}.featured`)}
                    onChange={(e) => set(`packages.plans.${i}.featured`, e.target.checked)}
                    data-testid={`plan-${i}-featured`} className="h-4 w-4 accent-[#7A2B21]" />
                  Highlight as "Most Chosen"
                </label>
              </Card>
            ))}
          </>
        )}

        {tab === "story" && (
          <>
            <Card title="Our Promise (3 Chapters)">
              <F label="Kicker" path="manifesto.kicker" />
              <div className="grid gap-5 sm:grid-cols-2">
                <F label="Title part 1" path="manifesto.title_line1" />
                <F label="Title part 2 (red italic)" path="manifesto.title_line2" />
              </div>
              <F label="Intro paragraph" path="manifesto.intro" textarea />
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-3 rounded-xl bg-[#FAF8F5] p-4">
                  <F label={`Chapter ${i + 1} title`} path={`manifesto.chapters.${i}.title`} />
                  <F label="Text" path={`manifesto.chapters.${i}.body`} textarea />
                </div>
              ))}
            </Card>
            <Card title="Moving Ribbon (Marquee)">
              <Lines label="Ribbon phrases" path="marquee.items" hint="One phrase per line. Mix English and Punjabi freely." />
            </Card>
            <Card title="How It Works">
              <F label="Kicker" path="how.kicker" />
              <div className="grid gap-5 sm:grid-cols-2">
                <F label="Title part 1" path="how.title_line1" />
                <F label="Title part 2 (red italic)" path="how.title_line2" />
              </div>
              <F label="Button label" path="how.cta_label" />
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="space-y-3 rounded-xl bg-[#FAF8F5] p-4">
                  <F label={`Step ${i + 1} title`} path={`how.steps.${i}.title`} />
                  <F label="Text" path={`how.steps.${i}.body`} textarea />
                </div>
              ))}
            </Card>
          </>
        )}

        {tab === "trust" && (
          <>
            <Card title="Trust Section">
              <F label="Kicker" path="trust.kicker" />
              <div className="grid gap-5 sm:grid-cols-2">
                <F label="Title part 1" path="trust.title_line1" />
                <F label="Title part 2 (red italic)" path="trust.title_line2" />
              </div>
              <F label="Intro" path="trust.intro" textarea />
              <Lines label="Trust badges (4 short phrases)" path="trust.badges" />
            </Card>
            <Card title="Sample Visit Report">
              <F label="Report location line" path="trust.report.location" />
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="grid gap-3 rounded-xl bg-[#FAF8F5] p-4 sm:grid-cols-2">
                  <F label={`Row ${i + 1} label`} path={`trust.report.rows.${i}.label`} />
                  <F label="Value" path={`trust.report.rows.${i}.value`} />
                </div>
              ))}
              <F label="Photos note" path="trust.report.photos_note" />
            </Card>
            <Card title="Founding Promises">
              <F label="Small heading above promises" path="trust.commitments_title" />
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-3 rounded-xl bg-[#FAF8F5] p-4">
                  <F label={`Promise ${i + 1} text`} path={`trust.commitments.${i}.quote`} textarea />
                  <F label="Signature line" path={`trust.commitments.${i}.name`} />
                </div>
              ))}
            </Card>
          </>
        )}

        {tab === "faq" && (
          <Card title="Frequently Asked Questions">
            <p className="-mt-2 rounded-lg bg-[#C5A059]/10 px-4 py-2.5 text-[11px] leading-relaxed text-[#57534E]">
              Tip: add a link inside any answer like this — [see our property page](/nri-property-management)
            </p>
            <div className="grid gap-5 sm:grid-cols-2">
              <F label="Title part 1" path="faq.title_line1" />
              <F label="Title part 2 (red italic)" path="faq.title_line2" />
            </div>
            {get("faq.items").map((item, i) => (
              <div key={i} className="space-y-3 rounded-xl bg-[#FAF8F5] p-4" data-testid={`faq-editor-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-3">
                    <F label={`Question ${i + 1}`} path={`faq.items.${i}.q`} />
                    <F label="Answer" path={`faq.items.${i}.a`} textarea />
                  </div>
                  <button
                    onClick={() => set("faq.items", get("faq.items").filter((_, j) => j !== i))}
                    data-testid={`faq-delete-${i}`}
                    className="mt-6 rounded-full p-2 text-[#7A2B21] transition-colors hover:bg-[#7A2B21]/10"
                    aria-label="Delete question">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
            <button
              onClick={() => set("faq.items", [...get("faq.items"), { q: "New question?", a: "Answer..." }])}
              data-testid="faq-add-button"
              className="inline-flex items-center gap-2 rounded-full border border-[#2C4C3B] px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#2C4C3B] transition-colors hover:bg-[#2C4C3B] hover:text-white">
              <Plus size={13} /> Add Question
            </button>
          </Card>
        )}

        {tab === "contact" && (
          <>
            <Card title="Contact Details (shown everywhere on the site)">
              <F label="Phone number (display)" path="business.phone_display" hint="e.g. +91 98912 43309" />
              <F label="WhatsApp number (digits only, with country code)" path="business.whatsapp_number" hint="e.g. 919891243309 — no + or spaces" />
              <F label="Email that receives enquiries" path="business.enquiry_email" hint="Every website enquiry is emailed here." />
            </Card>
            <Card title="Enquiry Section Text">
              <F label="Kicker" path="enquiry.kicker" />
              <div className="grid gap-5 sm:grid-cols-2">
                <F label="Title part 1" path="enquiry.title_line1" />
                <F label="Title part 2 (gold italic)" path="enquiry.title_line2" />
              </div>
              <F label="Intro" path="enquiry.intro" textarea />
              <F label="WhatsApp pre-filled message" path="enquiry.whatsapp_msg" textarea />
            </Card>
            <Card title="Footer">
              <F label="Company tagline" path="site.tagline" hint="Shown under your logo and in the footer." />
              <F label="Punjabi tagline" path="footer.punjabi_tagline" />
              <F label="Short description" path="footer.blurb" textarea />
            </Card>
          </>
        )}

        {tab === "dashboard" && <DashboardTab />}
        {tab === "business" && <BusinessTab F={F} Img={Img} Card={Card} />}
        {tab === "seo" && <SeoTab get={get} set={set} F={F} Card={Card} />}
        {tab === "nav" && <NavTab get={get} set={set} Card={Card} />}
        {tab === "sections" && <SectionsTab get={get} set={set} Card={Card} />}
        {tab === "redirects" && <RedirectsTab get={get} set={set} Card={Card} />}
        {tab === "pages" && <PagesTab get={get} set={set} F={F} Lines={Lines} Card={Card} Img={Img} />}
        {tab === "leads" && <LeadsTab />}
        {tab === "account" && <AccountTab email={auth.email} onLogout={logout} />}
      </main>
    </div>
  );
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      onSuccess(data);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#1C1917] px-6" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl bg-[#FAF8F5] p-10 shadow-2xl" data-testid="admin-login-form">
        <p className="font-display text-2xl font-semibold tracking-tight text-[#1C1917]">Tricity Family Concierge</p>
        <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-[#7A2B21]">Admin Login</p>
        <div className="mt-8 space-y-4">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" className={inputCls} data-testid="admin-email-input" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" className={inputCls} data-testid="admin-password-input" />
        </div>
        {error && <p className="mt-4 text-xs font-semibold text-[#7A2B21]" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-submit"
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#7A2B21] py-3.5 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#5C1E16] disabled:opacity-60">
          {busy && <Loader2 size={14} className="animate-spin" />}
          {busy ? "Signing in..." : "Sign In"}
        </button>
        <a href="/" className="mt-5 block text-center text-xs text-[#57534E] hover:text-[#7A2B21]">← Back to website</a>
      </form>
    </div>
  );
}

const LEAD_STATUSES = ["New", "Contacted", "Consultation", "Quotation Sent", "Converted", "Closed"];

const STATUS_COLORS = {
  New: "bg-[#C5A059]/20 text-[#7A2B21]",
  Contacted: "bg-blue-100 text-blue-800",
  Consultation: "bg-purple-100 text-purple-800",
  "Quotation Sent": "bg-amber-100 text-amber-800",
  Converted: "bg-green-100 text-green-800",
  Closed: "bg-gray-200 text-gray-600",
};

function LeadsTab() {
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const load = () =>
    axios
      .get(`${API}/admin/leads`, { params: { q: q || undefined, status: statusFilter } })
      .then((r) => setLeads(r.data.leads))
      .catch(() => setLeads([]));

  useEffect(() => { load(); }, [statusFilter]);

  const search = (e) => {
    e.preventDefault();
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete — please try again.");
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}/status`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/leads/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tricity-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported as CSV.");
    } catch {
      toast.error("Export failed — please try again.");
    }
  };

  if (leads === null) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#7A2B21]" size={28} /></div>;
  }

  return (
    <div className="rounded-2xl border border-[#1C1917]/10 bg-white p-6 shadow-[0_10px_30px_rgba(28,25,23,0.05)]" data-testid="leads-table">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-medium text-[#1C1917]">Website Enquiries ({leads.length})</h3>
          <p className="text-xs text-[#57534E]">Newest first. Every enquiry is also emailed to you.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <form onSubmit={search} className="flex items-center gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / phone"
              className="rounded-lg border border-[#1C1917]/15 bg-white px-4 py-2 text-xs" data-testid="leads-search" />
            <button type="submit" className="rounded-full border border-[#1C1917]/15 p-2 hover:border-[#7A2B21]" data-testid="leads-search-button" aria-label="Search">
              <Search size={14} />
            </button>
          </form>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-[#1C1917]/15 bg-white px-3 py-2 text-xs" data-testid="leads-status-filter">
            <option value="All">All statuses</option>
            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={exportCsv} data-testid="leads-export-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#2C4C3B] px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#1f3529]">
            <Download size={13} /> Export CSV
          </button>
        </div>
      </div>
      {leads.length === 0 ? (
        <p className="py-10 text-center text-sm text-[#57534E]" data-testid="leads-empty">No enquiries match — they'll appear here the moment someone writes in.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#1C1917]/10 text-[10px] uppercase tracking-wider text-[#57534E]">
                <th className="pb-3 pr-4">Date</th><th className="pb-3 pr-4">Name</th>
                <th className="pb-3 pr-4">Contact</th><th className="pb-3 pr-4">Country</th>
                <th className="pb-3 pr-4">Parents In</th><th className="pb-3 pr-4">Property</th>
                <th className="pb-3 pr-4">Service</th><th className="pb-3 pr-4">Status</th>
                <th className="pb-3">Message</th><th className="pb-3 pl-3"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l, i) => (
                <tr key={l.id || i} className="border-b border-[#1C1917]/5 align-top" data-testid={`lead-row-${i}`}>
                  <td className="py-3 pr-4 text-xs text-[#57534E]">{new Date(l.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</td>
                  <td className="py-3 pr-4 font-semibold text-[#1C1917]">{l.name}</td>
                  <td className="py-3 pr-4 text-xs">{l.email}<br />{l.phone}</td>
                  <td className="py-3 pr-4 text-xs">{l.country || "—"}</td>
                  <td className="py-3 pr-4 text-xs">{l.parents_city || "—"}</td>
                  <td className="py-3 pr-4 text-xs">{l.property_location || "—"}</td>
                  <td className="py-3 pr-4 text-xs font-semibold text-[#7A2B21]">{l.service_required || l.package_interest || "—"}</td>
                  <td className="py-3 pr-4">
                    <select
                      value={l.status || "New"}
                      onChange={(e) => changeStatus(l.id, e.target.value)}
                      className={`rounded-full px-3 py-1.5 text-[11px] font-bold ${STATUS_COLORS[l.status || "New"]}`}
                      data-testid={`lead-status-${i}`}
                    >
                      {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="max-w-xs py-3 text-xs text-[#57534E]">{l.message || "—"}</td>
                  <td className="py-3 pl-3">
                    <button onClick={() => remove(l.id)} data-testid={`lead-delete-${i}`}
                      className="rounded-full p-2 text-[#7A2B21] transition-colors hover:bg-[#7A2B21]/10" aria-label="Delete enquiry">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AccountTab({ email, onLogout }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next });
      toast.success("Password changed! Please sign in with your new password.");
      setTimeout(onLogout, 1500);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md space-y-6">
    <div className="rounded-2xl border border-[#1C1917]/10 bg-white p-6 shadow-[0_10px_30px_rgba(28,25,23,0.05)]" data-testid="backup-card">
      <h3 className="mb-1 font-display text-lg font-medium text-[#1C1917]">Download Backup</h3>
      <p className="mb-6 text-xs leading-relaxed text-[#57534E]">
        Save everything on your website — all texts, packages, pages and customer enquiries —
        as one file on your computer. Do this monthly for peace of mind.
      </p>
      <button
        onClick={async () => {
          try {
            const r = await axios.get(`${API}/admin/backup`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `tricity-backup-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Backup downloaded — keep this file safe!");
          } catch {
            toast.error("Backup failed — please try again.");
          }
        }}
        data-testid="download-backup-button"
        className="inline-flex items-center gap-2 rounded-full bg-[#2C4C3B] px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#1f3529]"
      >
        <Save size={13} />
        Download Backup File
      </button>
    </div>
    <div className="rounded-2xl border border-[#1C1917]/10 bg-white p-6 shadow-[0_10px_30px_rgba(28,25,23,0.05)]" data-testid="account-tab">
      <h3 className="mb-1 font-display text-lg font-medium text-[#1C1917]">Change Your Password</h3>
      <p className="mb-6 text-xs text-[#57534E]">Signed in as {email}</p>
      <form onSubmit={submit} className="space-y-4">
        <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
          placeholder="Current password" className={inputCls} data-testid="current-password-input" />
        <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)}
          placeholder="New password (min 8 characters)" className={inputCls} data-testid="new-password-input" />
        <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm new password" className={inputCls} data-testid="confirm-password-input" />
        <button type="submit" disabled={busy} data-testid="change-password-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#7A2B21] px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#5C1E16] disabled:opacity-60">
          {busy && <Loader2 size={13} className="animate-spin" />}
          Update Password
        </button>
      </form>
    </div>
    </div>
  );
}

function PagesTab({ get, set, F, Lines, Card, Img }) {
  return (
    <>
      <Card title="Property Management Page — yourwebsite.com/nri-property-management">
        <F label="Hero small badge" path="property_page.kicker" />
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Headline part 1" path="property_page.headline_line1" />
          <F label="Headline part 2 (gold italic)" path="property_page.headline_line2" />
        </div>
        <F label="Intro paragraph" path="property_page.subtext" textarea />
        <Lines label="Trust strip (3 short phrases)" path="property_page.stats" />
        <F label="WhatsApp message" path="property_page.whatsapp_msg" textarea />
      </Card>
      <Card title="Risks Section (4 cards)">
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Title part 1" path="property_page.risks_title1" />
          <F label="Title part 2 (red italic)" path="property_page.risks_title2" />
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="grid gap-3 rounded-xl bg-[#FAF8F5] p-4 sm:grid-cols-2">
            <F label={`Risk ${i + 1} title`} path={`property_page.risks.${i}.title`} />
            <F label="Text" path={`property_page.risks.${i}.body`} />
          </div>
        ))}
      </Card>
      <Card title="Who Is This Service For? (3 cards)">
        <F label="Section label" path="property_page.audience.kicker" />
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Title part 1" path="property_page.audience.title1" />
          <F label="Title part 2 (red italic)" path="property_page.audience.title2" />
        </div>
        <F label="Intro (links allowed)" path="property_page.audience.intro" textarea />
        {[0, 1, 2].map((i) => (
          <div key={i} className="grid gap-3 rounded-xl bg-[#FAF8F5] p-4 sm:grid-cols-2">
            <F label={`Card ${i + 1} title`} path={`property_page.audience.items.${i}.title`} />
            <F label="Text" path={`property_page.audience.items.${i}.body`} />
          </div>
        ))}
      </Card>
      <Card title="What We Handle (services list)">
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Title part 1" path="property_page.services_title1" />
          <F label="Title part 2 (gold italic)" path="property_page.services_title2" />
        </div>
        <F label="Intro" path="property_page.services_intro" textarea />
        <Lines label="Services" path="property_page.services" hint="One service per line." />
      </Card>
      <Card title="How It Works (3 steps)">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-3 rounded-xl bg-[#FAF8F5] p-4">
            <F label={`Step ${i + 1} title`} path={`property_page.steps.${i}.title`} />
            <F label="Text" path={`property_page.steps.${i}.body`} textarea />
          </div>
        ))}
      </Card>
      <Card title="Questions (3)">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-3 rounded-xl bg-[#FAF8F5] p-4">
            <F label={`Question ${i + 1}`} path={`property_page.faqs.${i}.q`} />
            <F label="Answer" path={`property_page.faqs.${i}.a`} textarea />
          </div>
        ))}
      </Card>
      <Card title="Bottom Call-to-Action">
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Title part 1" path="property_page.cta_title1" />
          <F label="Title part 2 (gold italic)" path="property_page.cta_title2" />
        </div>
        <F label="Subtext" path="property_page.cta_subtext" textarea />
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Button label" path="property_page.cta_button" />
          <F label="Button WhatsApp message" path="property_page.cta_whatsapp_msg" />
        </div>
      </Card>
      <Card title="Parent Care Page — yourwebsite.com/nri-parent-care">
        <F label="Hero small badge" path="parent_care_page.kicker" />
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Headline part 1" path="parent_care_page.headline_line1" />
          <F label="Headline part 2 (gold italic)" path="parent_care_page.headline_line2" />
        </div>
        <F label="Intro paragraph" path="parent_care_page.subtext" textarea />
        <Lines label="Trust strip (3 short phrases)" path="parent_care_page.stats" />
        <Img label="Emotional photo (middle of page)" path="parent_care_page.image" />
        <F label="Quote under the photo" path="parent_care_page.image_quote" />
        <F label="WhatsApp message" path="parent_care_page.whatsapp_msg" textarea />
      </Card>
      <Card title="Parent Care — Worries Section (4 cards)">
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Title part 1" path="parent_care_page.worries_title1" />
          <F label="Title part 2 (red italic)" path="parent_care_page.worries_title2" />
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="grid gap-3 rounded-xl bg-[#FAF8F5] p-4 sm:grid-cols-2">
            <F label={`Worry ${i + 1} title`} path={`parent_care_page.worries.${i}.title`} />
            <F label="Text" path={`parent_care_page.worries.${i}.body`} />
          </div>
        ))}
      </Card>
      <Card title="Parent Care — Who Is This For (3 cards)">
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Title part 1" path="parent_care_page.audience.title1" />
          <F label="Title part 2 (red italic)" path="parent_care_page.audience.title2" />
        </div>
        <F label="Intro (links allowed)" path="parent_care_page.audience.intro" textarea />
        {[0, 1, 2].map((i) => (
          <div key={i} className="grid gap-3 rounded-xl bg-[#FAF8F5] p-4 sm:grid-cols-2">
            <F label={`Card ${i + 1} title`} path={`parent_care_page.audience.items.${i}.title`} />
            <F label="Text" path={`parent_care_page.audience.items.${i}.body`} />
          </div>
        ))}
      </Card>
      <Card title="Parent Care — Visit Includes & Steps & Questions">
        <Lines label="What every visit includes" path="parent_care_page.services" hint="One item per line." />
        <F label="Honest note (non-medical)" path="parent_care_page.honest_note" textarea />
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-3 rounded-xl bg-[#FAF8F5] p-4">
            <F label={`Step ${i + 1} title`} path={`parent_care_page.steps.${i}.title`} />
            <F label="Text" path={`parent_care_page.steps.${i}.body`} textarea />
          </div>
        ))}
        {[0, 1, 2].map((i) => (
          <div key={`f${i}`} className="space-y-3 rounded-xl bg-[#FAF8F5] p-4">
            <F label={`Question ${i + 1}`} path={`parent_care_page.faqs.${i}.q`} />
            <F label="Answer" path={`parent_care_page.faqs.${i}.a`} textarea />
          </div>
        ))}
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Bottom CTA title 1" path="parent_care_page.cta_title1" />
          <F label="Bottom CTA title 2 (gold italic)" path="parent_care_page.cta_title2" />
        </div>
        <F label="Bottom CTA subtext" path="parent_care_page.cta_subtext" textarea />
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Button label" path="parent_care_page.cta_button" />
          <F label="Button WhatsApp message" path="parent_care_page.cta_whatsapp_msg" />
        </div>
      </Card>
      <CustomPages />
    </>
  );
}

function CustomPages() {
  const [pages, setPages] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios.get(`${API}/admin/pages`).then((r) => setPages(r.data.pages)).catch(() => setPages([]));
  useEffect(() => { load(); }, []);

  const blank = { title: "", slug: "", subtitle: "", blocks: [{ heading: "", text: "" }], published: true };

  const setE = (key, val) => setEditing((p) => ({ ...p, [key]: val }));
  const setBlock = (i, key, val) =>
    setEditing((p) => {
      const blocks = p.blocks.map((b, j) => (j === i ? { ...b, [key]: val } : b));
      return { ...p, blocks };
    });

  const save = async () => {
    if (!editing.title.trim()) {
      toast.error("Please give the page a title.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing, slug: editing.slug || editing.title };
      if (editing.id) await axios.put(`${API}/admin/pages/${editing.id}`, payload);
      else await axios.post(`${API}/admin/pages`, payload);
      toast.success("Page saved — it's live!");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/admin/pages/${id}`);
      toast.success("Page deleted.");
      load();
    } catch {
      toast.error("Could not delete — please try again.");
    }
  };

  if (pages === null) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#7A2B21]" size={28} /></div>;
  }

  if (editing) {
    return (
      <div className="rounded-2xl border border-[#1C1917]/10 bg-white p-6 shadow-[0_10px_30px_rgba(28,25,23,0.05)]" data-testid="custom-page-editor">
        <h3 className="mb-5 font-display text-lg font-medium text-[#1C1917]">
          {editing.id ? "Edit Page" : "Create New Page"}
        </h3>
        <div className="space-y-5">
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#57534E]">Page title</span>
            <input className={inputCls} value={editing.title} onChange={(e) => setE("title", e.target.value)}
              placeholder="e.g. NRI Parent Care in Mohali" data-testid="custom-page-title" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#57534E]">Page URL (web address)</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#57534E]">/p/</span>
              <input className={inputCls} value={editing.slug} onChange={(e) => setE("slug", e.target.value)}
                placeholder="auto-created from title if left empty" data-testid="custom-page-slug" />
            </div>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#57534E]">Intro paragraph (optional)</span>
            <textarea rows={2} className={`${inputCls} resize-y`} value={editing.subtitle}
              onChange={(e) => setE("subtitle", e.target.value)} data-testid="custom-page-subtitle" />
          </label>
          <div>
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#57534E]">Content sections</span>
            <p className="mb-2 rounded-lg bg-[#C5A059]/10 px-4 py-2.5 text-[11px] leading-relaxed text-[#57534E]">
              Tip: add a link inside your text like this — [our packages](/#packages) or [another page](/p/your-page)
            </p>
            <div className="space-y-3">
              {editing.blocks.map((b, i) => (
                <div key={i} className="space-y-3 rounded-xl bg-[#FAF8F5] p-4" data-testid={`custom-block-${i}`}>
                  <div className="flex items-start gap-3">
                    <input className={inputCls} value={b.heading} placeholder="Section heading (optional)"
                      onChange={(e) => setBlock(i, "heading", e.target.value)} />
                    <button onClick={() => setE("blocks", editing.blocks.filter((_, j) => j !== i))}
                      className="mt-2 rounded-full p-2 text-[#7A2B21] hover:bg-[#7A2B21]/10" aria-label="Remove section"
                      data-testid={`custom-block-delete-${i}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <textarea rows={4} className={`${inputCls} resize-y`} value={b.text} placeholder="Write your content here..."
                    onChange={(e) => setBlock(i, "text", e.target.value)} />
                </div>
              ))}
            </div>
            <button onClick={() => setE("blocks", [...editing.blocks, { heading: "", text: "" }])}
              data-testid="custom-block-add"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#2C4C3B] px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#2C4C3B] transition-colors hover:bg-[#2C4C3B] hover:text-white">
              <Plus size={13} /> Add Section
            </button>
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-[#57534E]">
            <input type="checkbox" checked={!!editing.published} onChange={(e) => setE("published", e.target.checked)}
              className="h-4 w-4 accent-[#7A2B21]" data-testid="custom-page-published" />
            Published (visible to visitors)
          </label>
          <div className="rounded-xl border border-[#1C1917]/10 p-4">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-[#57534E]">Page SEO (optional)</p>
            <div className="space-y-3">
              <input className={inputCls} value={editing.seo_title || ""} placeholder="SEO title (Google headline)"
                onChange={(e) => setE("seo_title", e.target.value)} data-testid="custom-page-seo-title" />
              <textarea rows={2} className={`${inputCls} resize-y`} value={editing.seo_description || ""}
                placeholder="Meta description (Google snippet)" onChange={(e) => setE("seo_description", e.target.value)}
                data-testid="custom-page-seo-desc" />
              <label className="flex items-center gap-2 text-xs font-semibold text-[#57534E]">
                <input type="checkbox" checked={!!editing.noindex} onChange={(e) => setE("noindex", e.target.checked)}
                  className="h-4 w-4 accent-[#7A2B21]" data-testid="custom-page-noindex" />
                Hide this page from Google (noindex)
              </label>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving} data-testid="custom-page-save"
              className="inline-flex items-center gap-2 rounded-full bg-[#7A2B21] px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#5C1E16] disabled:opacity-60">
              {saving && <Loader2 size={13} className="animate-spin" />}
              {saving ? "Saving..." : "Save Page"}
            </button>
            <button onClick={() => setEditing(null)} data-testid="custom-page-cancel"
              className="rounded-full border border-[#1C1917]/15 px-6 py-3 text-xs font-bold uppercase tracking-wider text-[#1C1917] hover:border-[#7A2B21]">
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#1C1917]/10 bg-white p-6 shadow-[0_10px_30px_rgba(28,25,23,0.05)]" data-testid="custom-pages-list">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-medium text-[#1C1917]">Your Extra Pages</h3>
          <p className="text-xs text-[#57534E]">Create unlimited pages — great for new services or Google keywords.</p>
        </div>
        <button onClick={() => setEditing({ ...blank })} data-testid="new-page-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#2C4C3B] px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#1f3529]">
          <Plus size={13} /> New Page
        </button>
      </div>
      {pages.length === 0 ? (
        <p className="py-8 text-center text-sm text-[#57534E]" data-testid="custom-pages-empty">
          No extra pages yet. Click "New Page" to create your first one.
        </p>
      ) : (
        <div className="space-y-3">
          {pages.map((pg, i) => (
            <div key={pg.id} className="flex items-center justify-between rounded-xl bg-[#FAF8F5] px-5 py-4" data-testid={`custom-page-row-${i}`}>
              <div>
                <p className="text-sm font-semibold text-[#1C1917]">{pg.title}</p>
                <a href={`/p/${pg.slug}`} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-[#7A2B21] underline-offset-2 hover:underline">
                  /p/{pg.slug} {pg.published ? "" : "· (hidden)"}
                </a>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(pg)} data-testid={`custom-page-edit-${i}`}
                  className="rounded-full border border-[#1C1917]/15 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#1C1917] hover:border-[#7A2B21] hover:text-[#7A2B21]">
                  Edit
                </button>
                <button onClick={() => del(pg.id)} data-testid={`custom-page-delete-${i}`}
                  className="rounded-full p-2 text-[#7A2B21] hover:bg-[#7A2B21]/10" aria-label="Delete page">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SECTION_NAMES = {
  promise: "Our Promise (3 chapters)",
  serve: "Who We Serve",
  services: "Services Grid",
  how: "How It Works",
  packages: "Packages",
  trust: "Trust & Sample Report",
  about_home: "About Preview",
  enquiry: "Enquiry Form",
  faq: "FAQ",
};

const moveItem = (arr, i, dir) => {
  const c = [...arr];
  const j = i + dir;
  if (j < 0 || j >= c.length) return c;
  [c[i], c[j]] = [c[j], c[i]];
  return c;
};

function DashboardTab() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stats`).then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  if (stats === null) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#7A2B21]" size={28} /></div>;
  }

  const cards = [
    ["Total Pages", stats.total_pages ?? "—"],
    ["Published Pages", stats.published_pages ?? "—"],
    ["Draft Pages", stats.draft_pages ?? 0],
    ["Total Leads", stats.total_leads ?? 0],
    ["New Leads", stats.new_leads ?? 0],
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      {stats.unpublished_changes && (
        <div className="rounded-2xl border border-[#C5A059] bg-[#C5A059]/10 p-5 text-sm font-semibold text-[#7A2B21]" data-testid="dashboard-draft-notice">
          You have unpublished changes. Press "Publish Live" (top right) when you're ready for visitors to see them.
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {cards.map(([label, value], i) => (
          <div key={label} className="rounded-2xl border border-[#1C1917]/10 bg-white p-5 shadow-[0_10px_30px_rgba(28,25,23,0.05)]" data-testid={`dashboard-stat-${i}`}>
            <p className="font-display text-3xl font-medium text-[#1C1917]">{value}</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#57534E]">{label}</p>
          </div>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-[#1C1917]/10 bg-white p-6 shadow-[0_10px_30px_rgba(28,25,23,0.05)]">
          <h3 className="mb-4 font-display text-lg font-medium text-[#1C1917]">Recent Enquiries</h3>
          {(stats.recent_leads || []).length === 0 ? (
            <p className="text-sm text-[#57534E]">No enquiries yet.</p>
          ) : (
            <div className="space-y-3">
              {stats.recent_leads.map((l, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl bg-[#FAF8F5] px-4 py-3" data-testid={`dashboard-lead-${i}`}>
                  <div>
                    <p className="text-sm font-semibold text-[#1C1917]">{l.name}</p>
                    <p className="text-xs text-[#57534E]">{l.service_required || l.package_interest || "General enquiry"}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-[10px] font-bold ${STATUS_COLORS[l.status || "New"]}`}>
                    {l.status || "New"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-[#1C1917]/10 bg-white p-6 shadow-[0_10px_30px_rgba(28,25,23,0.05)]">
          <h3 className="mb-4 font-display text-lg font-medium text-[#1C1917]">Site Health</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-[#57534E]">Sitemap</span>
              <span className="rounded-full bg-green-100 px-3 py-1 text-[10px] font-bold text-green-800" data-testid="dashboard-sitemap-status">OK — /sitemap.xml live</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#57534E]">Robots.txt</span>
              <span className="rounded-full bg-green-100 px-3 py-1 text-[10px] font-bold text-green-800">OK — admin blocked, site open</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#57534E]">SEO fields</span>
              <span className="rounded-full bg-green-100 px-3 py-1 text-[10px] font-bold text-green-800">Set for all main pages</span>
            </div>
            <p className="pt-2 text-[11px] leading-relaxed text-[#57534E]/80">
              Google indexing status is only known inside your Google Search Console account — we never claim indexing without it.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function BusinessTab({ F, Img, Card }) {
  return (
    <>
      <Card title="Identity (used across the whole website)">
        <F label="Business name" path="business.name" />
        <div className="grid gap-5 sm:grid-cols-2">
          <F label="Tagline" path="business.tagline" hint="e.g. Parents. Property. Peace of mind." />
          <F label="Brand promise" path="business.promise" hint='e.g. "You live abroad. We stay close."' />
        </div>
        <F label="Service areas" path="business.service_areas" hint="e.g. Chandigarh · Mohali · Panchkula" />
        <F label="Short business description" path="business.description" textarea />
        <F label="Website URL" path="business.website_url" hint="e.g. https://www.tricityfamilyconcierge.com" />
      </Card>
      <Card title="Contact Details (updates everywhere automatically)">
        <F label="Phone number (display)" path="business.phone_display" />
        <F label="WhatsApp number (digits only, with country code)" path="business.whatsapp_number" hint="e.g. 919891243309" />
        <F label="Public email" path="business.email" />
        <F label="Email that receives enquiries" path="business.enquiry_email" />
      </Card>
      <Card title="Social Media Links (icons appear in the footer when filled)">
        <F label="Instagram URL" path="business.instagram" hint="e.g. https://instagram.com/tricityfamilyconcierge" />
        <F label="Facebook URL" path="business.facebook" />
        <F label="LinkedIn URL" path="business.linkedin" />
      </Card>
      <Card title="Branding">
        <Img label="Logo (shown next to your name in the header)" path="business.logo_url" />
        <Img label="Favicon (small browser-tab icon, square image)" path="business.favicon_url" />
      </Card>
    </>
  );
}

const SEO_PAGES = [
  ["/", "Home"],
  ["/nri-parent-care", "NRI Parent Care"],
  ["/nri-property-management", "NRI Property Management"],
  ["/services", "Services"],
  ["/about", "About"],
  ["/contact", "Contact"],
];

const H1_SOURCES = {
  "/": (get) => [get("hero.h1_line1"), get("hero.h1_line2")].filter(Boolean).join(" "),
  "/nri-parent-care": (get) => [get("parent_care_page.headline_line1"), get("parent_care_page.headline_line2")].filter(Boolean).join(" "),
  "/nri-property-management": (get) => [get("property_page.headline_line1"), get("property_page.headline_line2")].filter(Boolean).join(" "),
  "/services": (get) => get("services_page.h1"),
  "/about": (get) => get("about_page.h1"),
  "/contact": (get) => get("contact_page.h1"),
};

const Chip = ({ ok, warn }) => (
  <span className={`rounded-full px-3 py-1 text-[10px] font-bold ${ok ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
    {ok ? "OK" : warn || "Needs attention"}
  </span>
);

function SeoTab({ get, set, F, Card }) {
  const [page, setPage] = useState("/");
  const [robots, setRobots] = useState("");

  useEffect(() => {
    fetch("/robots.txt").then((r) => r.text()).then(setRobots).catch(() => setRobots("Could not load robots.txt"));
  }, []);

  const seo = get(`seo.${page}`) || {};
  const titleLen = (seo.title || "").length;
  const descLen = (seo.description || "").length;

  return (
    <>
      <Card title="SEO Editor — pick a page, edit its Google fields">
        <label className="block" data-testid="seo-page-select-wrap">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#57534E]">Page</span>
          <select value={page} onChange={(e) => setPage(e.target.value)} className={inputCls} data-testid="seo-page-select">
            {SEO_PAGES.map(([path, label]) => <option key={path} value={path}>{`${label} (${path})`}</option>)}
          </select>
        </label>
        <div>
          <F label="SEO Title" path={`seo.${page}.title`} />
          <p className={`mt-1 text-[11px] font-semibold ${titleLen > 60 ? "text-amber-600" : "text-green-700"}`} data-testid="seo-title-count">
            {titleLen}/60 characters {titleLen > 60 ? "— a bit long, Google may cut it" : "— good length"}
          </p>
        </div>
        <div>
          <F label="Meta Description" path={`seo.${page}.description`} textarea />
          <p className={`mt-1 text-[11px] font-semibold ${descLen > 160 ? "text-amber-600" : "text-green-700"}`} data-testid="seo-desc-count">
            {descLen}/160 characters {descLen > 160 ? "— a bit long" : "— good length"}
          </p>
        </div>
        <F label="Canonical URL" path={`seo.${page}.canonical`} hint="The one true address of this page. Keep the www version." />
        <F label="Social share image path (optional)" path={`seo.${page}.og_image`} hint="e.g. /images/hero-parents.jpg — shown when the link is shared on WhatsApp/Facebook" />
        <label className="flex items-center gap-2 text-xs font-semibold text-[#57534E]">
          <input type="checkbox" checked={!!seo.noindex} onChange={(e) => set(`seo.${page}.noindex`, e.target.checked)}
            className="h-4 w-4 accent-[#7A2B21]" data-testid="seo-noindex" />
          Hide this page from Google (noindex)
        </label>
        <p className="rounded-lg bg-[#C5A059]/10 px-4 py-2.5 text-[11px] leading-relaxed text-[#57534E]">
          Your edits are never overwritten automatically. Remember: Save Draft → Preview → Publish Live.
        </p>
      </Card>

      <Card title="SEO Overview — all pages at a glance">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs" data-testid="seo-overview-table">
            <thead>
              <tr className="border-b border-[#1C1917]/10 text-[10px] uppercase tracking-wider text-[#57534E]">
                <th className="pb-3 pr-4">Page</th><th className="pb-3 pr-4">H1</th><th className="pb-3 pr-4">Title</th>
                <th className="pb-3 pr-4">Description</th><th className="pb-3 pr-4">Canonical</th><th className="pb-3">Google</th>
              </tr>
            </thead>
            <tbody>
              {SEO_PAGES.map(([path, label]) => {
                const s = get(`seo.${path}`) || {};
                const h1 = H1_SOURCES[path] ? H1_SOURCES[path](get) : "";
                return (
                  <tr key={path} className="border-b border-[#1C1917]/5" data-testid={`seo-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <td className="py-3 pr-4 font-semibold text-[#1C1917]">{label}<br /><span className="font-normal text-[#57534E]">{path}</span></td>
                    <td className="max-w-[180px] py-3 pr-4 text-[#57534E]">{h1 || "—"}</td>
                    <td className="py-3 pr-4"><Chip ok={(s.title || "").length >= 10 && (s.title || "").length <= 60} warn={(s.title || "").length > 60 ? "Too long" : "Missing"} /></td>
                    <td className="py-3 pr-4"><Chip ok={(s.description || "").length >= 50 && (s.description || "").length <= 160} warn={(s.description || "").length > 160 ? "Too long" : "Missing"} /></td>
                    <td className="py-3 pr-4"><Chip ok={!!s.canonical} warn="Missing" /></td>
                    <td className="py-3"><Chip ok={!s.noindex} warn="Noindex" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="robots.txt (read-only — for your safety)">
        <pre className="overflow-x-auto rounded-xl bg-[#1C1917] p-5 text-xs leading-relaxed text-[#FAF8F5]/90" data-testid="robots-viewer">{robots}</pre>
        <p className="text-[11px] leading-relaxed text-[#57534E]">
          This file tells Google what it may crawl. It is intentionally not editable here — a wrong line can hide your
          whole website from Google. If you ever need it changed, ask support and it will be done carefully.
        </p>
      </Card>
    </>
  );
}

function NavTab({ get, set, Card }) {
  const items = get("navigation") || [];
  const setItems = (v) => set("navigation", v);
  return (
    <Card title="Navigation Menu (top of every page)">
      <p className="-mt-2 rounded-lg bg-[#C5A059]/10 px-4 py-2.5 text-[11px] leading-relaxed text-[#57534E]">
        Links starting with / open a page (e.g. /about). Links starting with # scroll on the homepage (e.g. #packages).
      </p>
      {items.map((item, i) => (
        <div key={i} className="flex flex-wrap items-end gap-3 rounded-xl bg-[#FAF8F5] p-4" data-testid={`nav-item-${i}`}>
          <label className="flex-1">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[#57534E]">Label</span>
            <input className={inputCls} value={item.label}
              onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              data-testid={`nav-label-${i}`} />
          </label>
          <label className="flex-1">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[#57534E]">Link</span>
            <input className={inputCls} value={item.href}
              onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, href: e.target.value } : x)))}
              data-testid={`nav-href-${i}`} />
          </label>
          <div className="flex gap-1">
            <button onClick={() => setItems(moveItem(items, i, -1))} className="rounded-full p-2 hover:bg-[#1C1917]/5" aria-label="Move up" data-testid={`nav-up-${i}`}><ArrowUp size={14} /></button>
            <button onClick={() => setItems(moveItem(items, i, 1))} className="rounded-full p-2 hover:bg-[#1C1917]/5" aria-label="Move down" data-testid={`nav-down-${i}`}><ArrowDown size={14} /></button>
            <button onClick={() => { if (window.confirm(`Remove "${item.label}" from the menu?`)) setItems(items.filter((_, j) => j !== i)); }}
              className="rounded-full p-2 text-[#7A2B21] hover:bg-[#7A2B21]/10" aria-label="Remove" data-testid={`nav-delete-${i}`}><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
      <button onClick={() => setItems([...items, { label: "New Item", href: "/" }])} data-testid="nav-add-button"
        className="inline-flex items-center gap-2 rounded-full border border-[#2C4C3B] px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#2C4C3B] transition-colors hover:bg-[#2C4C3B] hover:text-white">
        <Plus size={13} /> Add Menu Item
      </button>
    </Card>
  );
}

function SectionsTab({ get, set, Card }) {
  const sections = get("homepage_sections") || [];
  const setSections = (v) => set("homepage_sections", v);
  return (
    <Card title="Homepage Sections — show, hide and reorder">
      <p className="-mt-2 rounded-lg bg-[#C5A059]/10 px-4 py-2.5 text-[11px] leading-relaxed text-[#57534E]">
        Uncheck a section to hide it from the homepage. Use arrows to change the order. Changes go live on Publish.
      </p>
      {sections.map((s, i) => (
        <div key={s.id} className="flex items-center justify-between rounded-xl bg-[#FAF8F5] px-5 py-4" data-testid={`section-row-${s.id}`}>
          <label className="flex items-center gap-3 text-sm font-semibold text-[#1C1917]">
            <input type="checkbox" checked={!!s.enabled}
              onChange={(e) => setSections(sections.map((x, j) => (j === i ? { ...x, enabled: e.target.checked } : x)))}
              className="h-4 w-4 accent-[#2C4C3B]" data-testid={`section-toggle-${s.id}`} />
            {SECTION_NAMES[s.id] || s.id}
          </label>
          <div className="flex gap-1">
            <button onClick={() => setSections(moveItem(sections, i, -1))} className="rounded-full p-2 hover:bg-[#1C1917]/5" aria-label="Move up" data-testid={`section-up-${s.id}`}><ArrowUp size={14} /></button>
            <button onClick={() => setSections(moveItem(sections, i, 1))} className="rounded-full p-2 hover:bg-[#1C1917]/5" aria-label="Move down" data-testid={`section-down-${s.id}`}><ArrowDown size={14} /></button>
          </div>
        </div>
      ))}
    </Card>
  );
}

function RedirectsTab({ get, set, Card }) {
  const redirects = get("redirects") || [];
  const setRedirects = (v) => set("redirects", v);
  return (
    <Card title="Redirects — forward old addresses to new ones (301)">
      <p className="-mt-2 rounded-lg bg-[#C5A059]/10 px-4 py-2.5 text-[11px] leading-relaxed text-[#57534E]">
        Example: from <b>/old-page</b> to <b>/nri-parent-care</b>. Anyone visiting the old address lands on the new one,
        and Google transfers the old page's value. Takes effect after Publish.
      </p>
      {redirects.length === 0 && (
        <p className="py-4 text-sm text-[#57534E]" data-testid="redirects-empty">No redirects yet.</p>
      )}
      {redirects.map((r, i) => (
        <div key={i} className="flex flex-wrap items-end gap-3 rounded-xl bg-[#FAF8F5] p-4" data-testid={`redirect-row-${i}`}>
          <label className="flex-1">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[#57534E]">From (old URL)</span>
            <input className={inputCls} value={r.from} placeholder="/old-page"
              onChange={(e) => setRedirects(redirects.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))}
              data-testid={`redirect-from-${i}`} />
          </label>
          <label className="flex-1">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[#57534E]">To (new URL)</span>
            <input className={inputCls} value={r.to} placeholder="/nri-parent-care"
              onChange={(e) => setRedirects(redirects.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))}
              data-testid={`redirect-to-${i}`} />
          </label>
          <button onClick={() => { if (window.confirm(`Delete redirect ${r.from} → ${r.to}?`)) setRedirects(redirects.filter((_, j) => j !== i)); }}
            className="rounded-full p-2 text-[#7A2B21] hover:bg-[#7A2B21]/10" aria-label="Delete redirect" data-testid={`redirect-delete-${i}`}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button onClick={() => setRedirects([...redirects, { from: "", to: "" }])} data-testid="redirect-add-button"
        className="inline-flex items-center gap-2 rounded-full border border-[#2C4C3B] px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#2C4C3B] transition-colors hover:bg-[#2C4C3B] hover:text-white">
        <Plus size={13} /> Add Redirect
      </button>
    </Card>
  );
}
